"use client";
import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import Image from "next/image";
import { useTranslation } from "react-i18next";

// ─── Assets ─────────────────────────────────────────────────────────────────
const BG_IMG = "https://getgeek-website-assets.b-cdn.net/BGImg2Shades.webp";
const LEFT_IMG = "https://getgeek-website-assets.b-cdn.net/TechnologyLeftImage.webp";
const BG_IMG_AR = "https://getgeek-website-assets.b-cdn.net/Getgeek%20Images/BGImg2ShadesArabic.webp";


// ─── Styled Components ──────────────────────────────────────────────────────
const AboutSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  position: "relative",
  backgroundImage: `url(${isRTL ? BG_IMG_AR : BG_IMG})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: "#0B0715",
  padding: "72px 0",
  scrollMarginTop: "90px",
  [theme.breakpoints.down("sm")]: {
    padding: "48px 0",
  },
}));
const AboutRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "48px",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "32px",
  },
}));

const LeftImageWrapper = styled(Box)(({ theme }) => ({
  flex: "0 0 45%",
  maxWidth: "45%",
  borderRadius: "16px",
  overflow: "hidden",
  lineHeight: 0,
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
  },
}));

const RightContent = styled(Box)(({ theme }) => ({
  flex: "0 0 55%",
  maxWidth: "55%",
  [theme.breakpoints.down("md")]: {
    flex: "0 0 100%",
    maxWidth: "100%",
  },
}));

const Eyebrow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "13px",
  fontWeight: 700,
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "#9048FB",
  marginBottom: "16px",
}));

const EyebrowDot = styled(Box)(() => ({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: "#9048FB",
  flexShrink: 0,
}));

const AboutHeading = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ theme, isRTL }) => ({
  fontFamily: isRTL ? "var(--font-plusjakarta)" : "var(--font-plusjakarta)",
  fontWeight: 700,
  fontSize: "35px",
  lineHeight: 1.3,
  color: "#FFFFFF",
  marginBottom: "20px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "26px",
  },
}));

const AboutParagraph = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "15px",
  lineHeight: 1.8,
  color: "#B8B4C4",
  marginBottom: "16px",
}));

const AboutFooterLine = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  fontFamily: isRTL ? "var(--font-outfit)" : "var(--font-outfit)",
  fontSize: "16px",
  fontWeight: 600,
  color: "#FFFFFF",
  marginTop: "5%",
}));

// ─── Component ───────────────────────────────────────────────────────────────
const LeapAboutSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  return (
    <AboutSection id="leap-2026-section" isRTL={isRTL}>
      <Container maxWidth="xl">
        <AboutRow>
          {/* Left Image */}
          <LeftImageWrapper>
            <Image
              src={LEFT_IMG}
              alt="LEAP 2026 technology event"
              width={640}
              height={420}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "cover",
              }}
            />
          </LeftImageWrapper>

          {/* Right Content */}
          <RightContent>
            <Eyebrow isRTL={isRTL}>
              <EyebrowDot />
              {t("leapAboutSection.eyebrow")}
            </Eyebrow>
            <AboutHeading variant="h2" isRTL={isRTL}>
              {t("leapAboutSection.heading")}
            </AboutHeading>

            <AboutParagraph isRTL={isRTL}>
              {t("leapAboutSection.paragraphOne")}
            </AboutParagraph>

            <AboutParagraph isRTL={isRTL}>
              {t("leapAboutSection.paragraphTwo")}
            </AboutParagraph>

            <AboutFooterLine isRTL={isRTL}>
              {t("leapAboutSection.footerLine")}
            </AboutFooterLine>
          </RightContent>
        </AboutRow>
      </Container>
    </AboutSection>
  );
};

export default LeapAboutSection;
